import React, { useState, Fragment } from 'react';
import { ShoppingCart, Trash2, Plus, Minus, CreditCard, Building2, CheckCircle, AlertCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
interface CheckoutProps {
  onNavigate: (page: string) => void;
}
export function Checkout({
  onNavigate
}: CheckoutProps) {
  const [step, setStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState<'credit' | 'transfer'>('credit');
  const cartItems = [{
    id: 'PRD-001',
    name: 'Indomie Goreng Special',
    sku: 'SKU-IM-001',
    price: 35000,
    quantity: 5,
    unit: 'Karton',
    moq: 100,
    subtotal: 175000
  }, {
    id: 'PRD-002',
    name: 'Aqua Galon 19L',
    sku: 'SKU-AQ-002',
    price: 22000,
    quantity: 3,
    unit: 'Galon',
    moq: 50,
    subtotal: 66000
  }, {
    id: 'PRD-003',
    name: 'Beras Ramos 5kg',
    sku: 'SKU-BR-005',
    price: 72000,
    quantity: 2,
    unit: 'Karung',
    moq: 200,
    subtotal: 144000
  }];
  const subtotal = cartItems.reduce((sum, item) => sum + item.subtotal, 0);
  const serviceFee = 3500 * cartItems.length;
  const shippingFee = 25000;
  const total = subtotal + serviceFee + shippingFee;
  const creditLimit = 50000000;
  const creditUsed = 37500000;
  const creditAvailable = creditLimit - creditUsed;
  return <div className="space-y-6" data-id="element-1421">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1422">
        <div data-id="element-1423">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-1424">Checkout</h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1425">
            Review pesanan dan selesaikan pembayaran
          </p>
        </div>
        <button onClick={() => onNavigate('catalog')} className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium" data-id="element-1426">
          <ArrowLeft className="w-4 h-4" data-id="element-1427" />
          Kembali ke Katalog
        </button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-center gap-4" data-id="element-1428">
        {[{
        num: 1,
        label: 'Review Pesanan'
      }, {
        num: 2,
        label: 'Informasi Pengiriman'
      }, {
        num: 3,
        label: 'Pembayaran'
      }].map((s, i) => <Fragment key={s.num} data-id="element-1429">
            <div className="flex items-center gap-3" data-id="element-1430">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${step >= s.num ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'}`} data-id="element-1431">
                {step > s.num ? <CheckCircle className="w-5 h-5" data-id="element-1432" /> : s.num}
              </div>
              <span className={`text-sm font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-1433">
                {s.label}
              </span>
            </div>
            {i < 2 && <div className={`w-16 h-1 ${step > s.num ? 'bg-blue-600' : 'bg-slate-200'}`} data-id="element-1434"></div>}
          </Fragment>)}
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-1435">
        {/* Main Content */}
        <div className="col-span-2 space-y-6" data-id="element-1436">
          {step === 1 /* Step 1: Review Items */ && <Card className="p-6" data-id="element-1437">
              <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-1438">
                Review Pesanan
              </h3>
              <div className="space-y-4" data-id="element-1439">
                {cartItems.map(item => <div key={item.id} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg" data-id="element-1440">
                    <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center" data-id="element-1441">
                      <ShoppingCart className="w-8 h-8 text-slate-400" data-id="element-1442" />
                    </div>
                    <div className="flex-1" data-id="element-1443">
                      <h4 className="font-bold text-slate-900" data-id="element-1444">{item.name}</h4>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-1445">
                        SKU: {item.sku}
                      </p>
                      <div className="flex items-center gap-4 mt-2" data-id="element-1446">
                        <span className="text-sm text-slate-600" data-id="element-1447">
                          Rp {item.price.toLocaleString('id-ID')} / {item.unit}
                        </span>
                        <Badge variant="neutral" className="text-xs" data-id="element-1448">
                          MOQ: {item.moq}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-1449">
                      <div className="flex items-center gap-2 mb-2" data-id="element-1450">
                        <button className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-50" data-id="element-1451">
                          <Minus className="w-4 h-4" data-id="element-1452" />
                        </button>
                        <span className="w-12 text-center font-bold" data-id="element-1453">
                          {item.quantity}
                        </span>
                        <button className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center hover:bg-slate-50" data-id="element-1454">
                          <Plus className="w-4 h-4" data-id="element-1455" />
                        </button>
                      </div>
                      <p className="text-lg font-bold text-slate-900" data-id="element-1456">
                        Rp {item.subtotal.toLocaleString('id-ID')}
                      </p>
                    </div>
                  </div>)}
              </div>
            </Card>}

          {step === 2 /* Step 2: Shipping Info */ && <Card className="p-6" data-id="element-1457">
              <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-1458">
                Informasi Pengiriman
              </h3>
              <div className="space-y-4" data-id="element-1459">
                <div data-id="element-1460">
                  <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-1461">
                    Alamat Pengiriman
                  </label>
                  <textarea defaultValue="Jl. Raya Desa No. 123, RT 01/RW 02, Kec. Sukamaju, Kab. Bogor, Jawa Barat 16710" rows={3} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1462" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-1463">
                  <div data-id="element-1464">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-1465">
                      Kontak Person
                    </label>
                    <input type="text" defaultValue="Budi Santoso" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1466" />
                  </div>
                  <div data-id="element-1467">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-1468">
                      Nomor Telepon
                    </label>
                    <input type="tel" defaultValue="+62 812-3456-7890" className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1469" />
                  </div>
                </div>

                <div data-id="element-1470">
                  <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-1471">
                    Catatan Pengiriman (Opsional)
                  </label>
                  <textarea placeholder="Tambahkan catatan khusus untuk pengiriman..." rows={2} className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1472" />
                </div>
              </div>
            </Card>}

          {step === 3 /* Step 3: Payment */ && <Card className="p-6" data-id="element-1473">
              <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-1474">
                Metode Pembayaran
              </h3>

              <div className="space-y-3 mb-6" data-id="element-1475">
                <button onClick={() => setPaymentMethod('credit')} className={`w-full p-4 rounded-lg border-2 transition-all ${paymentMethod === 'credit' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1476">
                  <div className="flex items-center gap-3" data-id="element-1477">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'credit' ? 'border-blue-600' : 'border-slate-300'}`} data-id="element-1478">
                      {paymentMethod === 'credit' && <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-1479"></div>}
                    </div>
                    <CreditCard className="w-6 h-6 text-slate-600" data-id="element-1480" />
                    <div className="text-left flex-1" data-id="element-1481">
                      <p className="font-bold text-slate-900" data-id="element-1482">
                        Kredit Koperasi
                      </p>
                      <p className="text-sm text-slate-600" data-id="element-1483">
                        Tersedia: Rp {creditAvailable.toLocaleString('id-ID')}
                      </p>
                    </div>
                    <Badge variant="success" data-id="element-1484">Recommended</Badge>
                  </div>
                </button>

                <button onClick={() => setPaymentMethod('transfer')} className={`w-full p-4 rounded-lg border-2 transition-all ${paymentMethod === 'transfer' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-1485">
                  <div className="flex items-center gap-3" data-id="element-1486">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'transfer' ? 'border-blue-600' : 'border-slate-300'}`} data-id="element-1487">
                      {paymentMethod === 'transfer' && <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-1488"></div>}
                    </div>
                    <Building2 className="w-6 h-6 text-slate-600" data-id="element-1489" />
                    <div className="text-left flex-1" data-id="element-1490">
                      <p className="font-bold text-slate-900" data-id="element-1491">Transfer Bank</p>
                      <p className="text-sm text-slate-600" data-id="element-1492">
                        Bayar via transfer bank (NET 30)
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {paymentMethod === 'credit' && creditAvailable < total && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-1493">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-1494" />
                  <div data-id="element-1495">
                    <p className="text-sm font-bold text-red-800" data-id="element-1496">
                      Limit Kredit Tidak Mencukupi
                    </p>
                    <p className="text-xs text-red-600 mt-1" data-id="element-1497">
                      Total pesanan melebihi limit kredit tersedia. Silakan
                      pilih metode pembayaran lain atau kurangi jumlah pesanan.
                    </p>
                  </div>
                </div>}
            </Card>}

          {/* Navigation Buttons */}
          <div className="flex gap-3" data-id="element-1498">
            {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} icon={<ArrowLeft className="w-4 h-4" data-id="element-1500" />} data-id="element-1499">
                Kembali
              </Button>}
            {step < 3 ? <Button variant="primary" onClick={() => setStep(step + 1)} fullWidth icon={<ArrowRight className="w-4 h-4" data-id="element-1502" />} data-id="element-1501">
                Lanjutkan
              </Button> : <Button variant="primary" onClick={() => {
            alert('Pesanan berhasil dibuat! (Demo Mode)');
            onNavigate('tracking');
          }} fullWidth icon={<CheckCircle className="w-4 h-4" data-id="element-1504" />} disabled={paymentMethod === 'credit' && creditAvailable < total} data-id="element-1503">
                Konfirmasi Pesanan
              </Button>}
          </div>
        </div>

        {/* Order Summary Sidebar */}
        <div data-id="element-1505">
          <Card className="p-6 sticky top-6" data-id="element-1506">
            <h3 className="font-bold text-lg text-slate-900 mb-4" data-id="element-1507">
              Ringkasan Pesanan
            </h3>

            <div className="space-y-3 mb-4 pb-4 border-b border-slate-200" data-id="element-1508">
              <div className="flex justify-between text-sm" data-id="element-1509">
                <span className="text-slate-600" data-id="element-1510">
                  Subtotal ({cartItems.length} item)
                </span>
                <span className="font-medium text-slate-900" data-id="element-1511">
                  Rp {subtotal.toLocaleString('id-ID')}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1512">
                <span className="text-slate-600" data-id="element-1513">Service Fee</span>
                <span className="font-medium text-slate-900" data-id="element-1514">
                  Rp {serviceFee.toLocaleString('id-ID')}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1515">
                <span className="text-slate-600" data-id="element-1516">Ongkir (JNE)</span>
                <span className="font-medium text-slate-900" data-id="element-1517">
                  Rp {shippingFee.toLocaleString('id-ID')}
                </span>
              </div>
            </div>

            <div className="flex justify-between items-center mb-6" data-id="element-1518">
              <span className="font-bold text-slate-900" data-id="element-1519">Total</span>
              <span className="text-2xl font-bold text-blue-600" data-id="element-1520">
                Rp {total.toLocaleString('id-ID')}
              </span>
            </div>

            {/* Credit Info */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1521">
              <p className="text-xs text-blue-700 mb-2" data-id="element-1522">
                Limit Kredit Koperasi
              </p>
              <div className="flex justify-between items-center mb-2" data-id="element-1523">
                <span className="text-sm text-blue-900" data-id="element-1524">Tersedia</span>
                <span className="text-sm font-bold text-blue-900" data-id="element-1525">
                  Rp {creditAvailable.toLocaleString('id-ID')}
                </span>
              </div>
              <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden" data-id="element-1526">
                <div className="h-full bg-blue-600 rounded-full" style={{
                width: `${(creditLimit - creditUsed) / creditLimit * 100}%`
              }} data-id="element-1527"></div>
              </div>
              <p className="text-xs text-blue-600 mt-2" data-id="element-1528">
                Sisa setelah pesanan: Rp{' '}
                {(creditAvailable - total).toLocaleString('id-ID')}
              </p>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}